import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Send, Trash2, Search } from "lucide-react";
import { fetchWithAuth } from "@/lib/auth-client";
import type {
  GRNListItemDto,
  GRNDetailDto,
  GRNLineItemDetailDto,
  CreateInvoiceFromGRsRequest,
  UpdateInvoiceRequest,
  InvoiceDetailDto,
  InvoiceSource,
} from "@procureflow/shared-types";
import {
  validateInvoiceFromGRRows,
  type InvoiceFromGRRow as InvoiceFromGRRowModel,
} from "@/lib/invoice-from-gr";
import { validateAttachment } from "@/lib/attachment-validator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// US-066 (m2m-document-linkage) — Convert-GR-to-Invoice builder.
//
// The terminal hop is MANDATORY and non-cancellable: a recorded goods receipt
// must become an invoice. This standalone form is reached from a "Create
// Invoice from GR" button on the invoice list and from a "Convert to Invoice"
// action on GR detail (which prefills the rows via ?grId=).
//
// Each line row has TWO cascading lookups: (1) pick a goods receipt that still
// needs invoicing (the picker lists GRs with has_invoice false), (2) pick a
// line of that GR. Quantity defaults to the line's received quantity (override
// allowed); unit price is an optional override (the BE defaults to the resolved
// PO line's price when omitted). The header carries the vendor invoice number,
// invoice date, due date (>= invoice date), and an optional source / attachment.
//
// On submit POST /api/invoices/from-grns and navigate to the new invoice.

export const Route = createFileRoute("/invoices_/from-grns")({
  // US-077 — ?editId=<invoiceId> opens this builder in draft-edit mode (from the Edit
  // affordance on a Recorded invoice). ?grId=<grnId> still prefills a new invoice.
  validateSearch: (search: Record<string, unknown>) => ({
    grId: typeof search.grId === "string" ? search.grId : "",
    editId: typeof search.editId === "string" ? search.editId : "",
  }),
  head: () => ({
    meta: [
      { title: "Create Invoice from GR — ProcureFlow" },
      {
        name: "description",
        content: "Convert one or more goods receipts into a vendor invoice.",
      },
    ],
  }),
  component: InvoiceFromGRsPage,
});

interface RowState {
  key: string;
  sourceGrId: string;
  sourceGrNumber: string;
  sourceGrLineItemId: string;
  lineDescription: string;
  /** received quantity on the GR line — the default + the ceiling shown. */
  receivedQty: number;
  /** quantity to bill (defaults to receivedQty, override allowed). */
  quantity: number;
  /** unit price override in cents; empty string = let the BE use the PO price. */
  unitPriceInput: string;
}

function newRow(): RowState {
  return {
    key: crypto.randomUUID(),
    sourceGrId: "",
    sourceGrNumber: "",
    sourceGrLineItemId: "",
    lineDescription: "",
    receivedQty: 0,
    quantity: 1,
    unitPriceInput: "",
  };
}

/** Today's date as YYYY-MM-DD (UTC) for the default invoice date. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function InvoiceFromGRsPage() {
  const { grId, editId } = useSearch({ from: Route.id });
  const isEdit = Boolean(editId);
  const navigate = useNavigate();

  const [grns, setGrns] = useState<GRNListItemDto[]>([]);
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(todayIso());
  const [source, setSource] = useState<InvoiceSource>("InternalEntry");
  const [rows, setRows] = useState<RowState[]>([newRow()]);
  // Cache of GRN detail (lines) by GRN id, lazily fetched when a GR is picked.
  const [grnDetails, setGrnDetails] = useState<Record<string, GRNDetailDto>>({});
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // US-035-style source attachment (optional).
  const [attachmentName, setAttachmentName] = useState("");
  const [attachmentDataUrl, setAttachmentDataUrl] = useState("");
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  async function ensureGrnDetail(id: string): Promise<GRNDetailDto | null> {
    if (grnDetails[id]) return grnDetails[id];
    const res = await fetchWithAuth(`/api/grns/${id}`);
    if (!res.ok) return null;
    const { data } = (await res.json()) as { data: GRNDetailDto };
    setGrnDetails((prev) => ({ ...prev, [id]: data }));
    return data;
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // fetchWithAuth awaits mswReady internally so the MSW mount-time fetch
        // pattern is satisfied. Only GRs that still need an invoice are listed.
        const res = await fetchWithAuth("/api/grns?uninvoiced=true&limit=100");
        const data = res.ok ? ((await res.json()) as { data: GRNListItemDto[] }).data : [];
        if (cancelled) return;
        setGrns(data ?? []);

        // US-077 edit mode — pre-load the existing invoice's header + lines. The source
        // GR(s) the invoice references are added to the picker list (they read as
        // "invoiced" so the uninvoiced query excludes them) so their lines resolve.
        if (editId) {
          const invRes = await fetchWithAuth(`/api/invoices/${editId}`);
          if (!cancelled && invRes.ok) {
            const { data: inv } = (await invRes.json()) as { data: InvoiceDetailDto };
            setVendorInvoiceNumber(inv.vendor_invoice_number);
            setInvoiceDate(inv.invoice_date);
            setDueDate(inv.due_date);
            setSource(inv.source);
            const sourceGrIds = [
              ...new Set(
                inv.line_items.map((li) => li.source_gr_id).filter((x): x is string => !!x),
              ),
            ];
            const grnDetailList = (
              await Promise.all(sourceGrIds.map((gid) => ensureGrnDetail(gid)))
            ).filter((d): d is GRNDetailDto => Boolean(d));
            if (!cancelled) {
              setGrns((prev) => {
                const seen = new Set(prev.map((g) => g.id));
                const extra = grnDetailList
                  .filter((d) => !seen.has(d.id))
                  .map(
                    (d): GRNListItemDto => ({
                      id: d.id,
                      grn_number: d.grn_number,
                      po_number: d.purchase_order?.po_number ?? "—",
                      vendor_name: d.purchase_order?.vendor_name ?? "—",
                      date_received: d.date_received,
                      line_count: d.line_items.length,
                      created_by_name: d.created_by_name,
                      status: d.status,
                      has_invoice: true,
                    }),
                  );
                return [...prev, ...extra];
              });
              const grnById = new Map(grnDetailList.map((d) => [d.id, d]));
              setRows(
                inv.line_items.map((li) => {
                  const grn = li.source_gr_id ? grnById.get(li.source_gr_id) : undefined;
                  return {
                    key: crypto.randomUUID(),
                    sourceGrId: li.source_gr_id ?? "",
                    sourceGrNumber: grn?.grn_number ?? "",
                    sourceGrLineItemId: li.source_gr_line_item_id ?? "",
                    lineDescription: li.description,
                    receivedQty: li.quantity,
                    quantity: li.quantity,
                    unitPriceInput: li.unit_price.toString(),
                  };
                }),
              );
            }
          }
        }

        // Prefill from a single GR when reached from GR detail.
        if (grId) {
          const detail = await ensureGrnDetail(grId);
          if (!cancelled && detail) {
            setRows(
              detail.line_items.map((li) => ({
                key: crypto.randomUUID(),
                sourceGrId: detail.id,
                sourceGrNumber: detail.grn_number,
                sourceGrLineItemId: li.id,
                lineDescription: li.po_line_description,
                receivedQty: li.quantity_received,
                quantity: li.quantity_received,
                unitPriceInput: "",
              })),
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grId, editId]);

  // Lines of a GR from the cached detail.
  function availableLines(grnId: string): GRNLineItemDetailDto[] {
    return grnDetails[grnId]?.line_items ?? [];
  }

  async function pickGr(rowKey: string, grn: GRNListItemDto) {
    await ensureGrnDetail(grn.id);
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourceGrId: grn.id,
              sourceGrNumber: grn.grn_number,
              // Reset the line selection when the GR changes.
              sourceGrLineItemId: "",
              lineDescription: "",
              receivedQty: 0,
            }
          : r,
      ),
    );
  }

  function pickLine(rowKey: string, line: GRNLineItemDetailDto) {
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              sourceGrLineItemId: line.id,
              lineDescription: line.po_line_description,
              receivedQty: line.quantity_received,
              quantity: line.quantity_received || 1,
            }
          : r,
      ),
    );
  }

  function updateQty(rowKey: string, qty: number) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, quantity: qty } : r)));
  }

  function updateUnitPrice(rowKey: string, value: string) {
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, unitPriceInput: value } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  function removeRow(rowKey: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== rowKey) : prev));
  }

  async function handleAttachmentChange(file: File | null) {
    setAttachmentError(null);
    if (!file) {
      setAttachmentName("");
      setAttachmentDataUrl("");
      return;
    }
    const validation = validateAttachment(file);
    if (!validation.ok) {
      setAttachmentError(validation.error);
      return;
    }
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsDataURL(file);
      });
      setAttachmentName(file.name);
      setAttachmentDataUrl(dataUrl);
    } catch {
      setAttachmentError("Failed to read file.");
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const modelRows: InvoiceFromGRRowModel[] = rows
      .filter((r) => r.sourceGrLineItemId)
      .map((r) => ({
        sourceGrId: r.sourceGrId,
        sourceGrLineItemId: r.sourceGrLineItemId,
        quantity: r.quantity,
        // An empty override means "use the PO price" — model it as 0 (a valid,
        // non-negative price) for client-side validation; we omit it on the wire.
        unitPrice: r.unitPriceInput.trim() === "" ? 0 : parseInt(r.unitPriceInput, 10),
      }));

    const validationError = validateInvoiceFromGRRows(modelRows, {
      vendorInvoiceNumber,
      invoiceDate,
      dueDate,
    });
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const lines = rows
        .filter((r) => r.sourceGrLineItemId)
        .map((r) => ({
          source_gr_line_item_id: r.sourceGrLineItemId,
          quantity: r.quantity,
          unit_price: r.unitPriceInput.trim() === "" ? undefined : parseInt(r.unitPriceInput, 10),
        }));
      if (isEdit && editId) {
        const body: UpdateInvoiceRequest = {
          vendor_invoice_number: vendorInvoiceNumber.trim(),
          invoice_date: invoiceDate,
          due_date: dueDate,
          source,
          attachment_url: attachmentDataUrl || undefined,
          lines,
        };
        const res = await fetchWithAuth(`/api/invoices/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errJson = (await res.json().catch(() => ({}))) as { message?: string };
          setFormError(errJson?.message ?? "Failed to save changes.");
          return;
        }
        navigate({ to: "/invoices/$id", params: { id: editId } });
        return;
      }
      const body: CreateInvoiceFromGRsRequest = {
        vendor_invoice_number: vendorInvoiceNumber.trim(),
        invoice_date: invoiceDate,
        due_date: dueDate,
        source,
        attachment_url: attachmentDataUrl || undefined,
        lines,
      };
      const res = await fetchWithAuth("/api/invoices/from-grns", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        setFormError(errJson?.message ?? "Failed to create the invoice.");
        return;
      }
      const okJson = (await res.json()) as { data: InvoiceDetailDto };
      navigate({ to: "/invoices/$id", params: { id: okJson.data.id } });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link
        to="/invoices"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Invoices
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-2">
        <h1 className="text-lg font-semibold tracking-tight">
          {isEdit ? "Edit draft invoice" : "Create Invoice from GR"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? "Adjust the header and billed lines. Saving re-resolves the purchase order lines and re-runs the match."
            : "Convert one or more goods receipts into a single vendor invoice. Each line traces back to the goods receipt line it was billed from. All selected receipts must belong to the same vendor."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Invoice header. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Invoice details</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              The vendor's invoice number, the invoice date, and when payment is due.
            </p>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="vendor-invoice-number">Vendor invoice number</Label>
              <Input
                id="vendor-invoice-number"
                value={vendorInvoiceNumber}
                onChange={(e) => setVendorInvoiceNumber(e.target.value)}
                placeholder="e.g. VEND-2026-00123"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invoice-date">Invoice date</Label>
              <Input
                id="invoice-date"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="due-date">Due date</Label>
              <Input
                id="due-date"
                type="date"
                value={dueDate}
                min={invoiceDate || undefined}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invoice-source">Source</Label>
              <Select value={source} onValueChange={(v) => setSource(v as InvoiceSource)}>
                <SelectTrigger id="invoice-source" aria-label="Source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="InternalEntry">Internal entry</SelectItem>
                  <SelectItem value="VendorEmail">Vendor email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
              <Label htmlFor="invoice-attachment">
                {source === "VendorEmail"
                  ? "Attach the vendor's invoice document"
                  : "Attachment (optional)"}
              </Label>
              <input
                id="invoice-attachment"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,application/pdf,image/png,image/jpeg"
                onChange={(e) => void handleAttachmentChange(e.target.files?.[0] ?? null)}
                className="w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-xs file:font-medium hover:file:bg-muted"
              />
              {attachmentName && !attachmentError && (
                <p className="text-xs text-muted-foreground">Attached: {attachmentName}</p>
              )}
              {attachmentError && (
                <p role="alert" className="text-xs text-destructive">
                  {attachmentError}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Line builder — two cascading lookups + qty / unit-price per row. */}
        <section className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Lines</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pick a goods receipt that still needs invoicing, then a line of that receipt.
                Quantity defaults to the quantity received; leave the unit price blank to bill at
                the purchase order price.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              data-testid="add-line-button"
            >
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/40">
                <tr>
                  <th className="px-4 py-2.5 text-left w-48">Goods receipt</th>
                  <th className="px-4 py-2.5 text-left">GR line</th>
                  <th className="px-3 py-2.5 text-left w-28">Received</th>
                  <th className="px-3 py-2.5 text-left w-24">Quantity</th>
                  <th className="px-3 py-2.5 text-left w-32">Unit price (¢)</th>
                  <th className="px-3 py-2.5 w-12" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.key} className="border-t border-border align-top">
                    {/* Lookup 1 — goods receipt that needs invoicing */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} goods receipt`}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition"
                          >
                            <span
                              className={row.sourceGrNumber ? "font-mono" : "text-muted-foreground"}
                            >
                              {row.sourceGrNumber || "Select a GR…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {grns.length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                No goods receipts are awaiting an invoice.
                              </p>
                            ) : (
                              <ul>
                                {grns.map((grn) => (
                                  <li key={grn.id}>
                                    <button
                                      type="button"
                                      data-testid={`gr-option-${grn.id}`}
                                      onClick={() => pickGr(row.key, grn)}
                                      className="grid w-full grid-cols-[8rem_minmax(0,1fr)] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate font-mono font-medium">
                                        {grn.grn_number}
                                      </span>
                                      <span className="truncate text-muted-foreground">
                                        {grn.vendor_name}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    {/* Lookup 2 — GR line */}
                    <td className="px-4 py-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Line ${idx + 1} GR line`}
                            disabled={!row.sourceGrId}
                            className="inline-flex w-full items-center justify-between gap-2 rounded border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className={row.lineDescription ? "" : "text-muted-foreground"}>
                              {row.lineDescription || "Select a line…"}
                            </span>
                            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-96 p-0 overflow-hidden">
                          <div className="max-h-60 overflow-y-auto">
                            {availableLines(row.sourceGrId).length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground">
                                This goods receipt has no lines.
                              </p>
                            ) : (
                              <ul>
                                {availableLines(row.sourceGrId).map((line) => (
                                  <li key={line.id}>
                                    <button
                                      type="button"
                                      data-testid={`gr-line-option-${line.id}`}
                                      onClick={() => pickLine(row.key, line)}
                                      className="grid w-full grid-cols-[minmax(0,1fr)_4rem] items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs transition hover:bg-accent first:border-t-0"
                                    >
                                      <span className="truncate">{line.po_line_description}</span>
                                      <span className="text-right tabular-nums text-muted-foreground">
                                        {line.quantity_received}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>

                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                      {row.sourceGrLineItemId ? row.receivedQty : "—"}
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} quantity`}
                        type="number"
                        min={1}
                        value={row.quantity}
                        disabled={!row.sourceGrLineItemId}
                        onChange={(e) => updateQty(row.key, parseInt(e.target.value, 10) || 0)}
                      />
                    </td>

                    <td className="px-3 py-2">
                      <Input
                        aria-label={`Line ${idx + 1} unit price`}
                        type="number"
                        min={0}
                        placeholder="PO price"
                        value={row.unitPriceInput}
                        disabled={!row.sourceGrLineItemId}
                        onChange={(e) => updateUnitPrice(row.key, e.target.value)}
                      />
                    </td>

                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        aria-label={`Remove line ${idx + 1}`}
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length === 1}
                        className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive transition disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/invoices">Cancel</Link>
          </Button>
          <Button type="submit" disabled={submitting} data-testid="submit-build-button">
            <Send className="h-3.5 w-3.5" />
            {submitting
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create Invoice"}
          </Button>
        </div>
      </form>
    </div>
  );
}
